import settings from '@/data/constants';
import { getToken } from '@/data/service/authService';
import { Logger } from '@/utils/logger';
import { apiSseContract } from '@montugroup/pms-api-contracts';
import type { SseConsumerMessage, SseLogMessage, SseWorkerMessage } from './protocol';
import { sendClientMessage } from './protocol';

export type SseEventName = SseConsumerMessage['domain'];
export type Message<Name extends SseEventName> = Extract<SseConsumerMessage, { domain: Name }>;
export type Listener<T extends SseEventName> = (message: Message<T>) => void;

/**
 * SSE Client manages the connection to the shared SSE Worker
 */
export class SseClient {
  private readonly logger = new Logger('SseClient');
  private readonly workerLogger = new Logger('SseWorker');

  worker: SharedWorker;
  listeners: Partial<{ [K in SseEventName]: Listener<K>[] }> = {};

  constructor() {
    this.logger.info('initializing SSE client');
    const worker = (this.worker = new SharedWorker(new URL('./sse-worker-entrypoint.ts', import.meta.url), {
      name: 'sse-worker',
      type: 'module',
    }));

    worker.addEventListener('error', (error) => this.handleError(error));
    worker.port.addEventListener('message', (event) => this.handleMessage(event));
    // emitted when the port fails to serialise or deserialise the message to the worker
    worker.port.addEventListener('messageerror', (event) =>
      this.logger.error('messageerror received from worker', { type: event.type, data: event.data }),
    );
    worker.port.start();
  }

  handleMessage(event: MessageEvent) {
    this.logger.debug('received worker message', event.type);
    const message = event.data as SseWorkerMessage;

    switch (message.domain) {
      case 'ping': {
        sendClientMessage(this.worker.port, { domain: 'pong' });
        break;
      }

      case 'sse_configuration_request': {
        sendClientMessage(this.worker.port, {
          domain: 'sse_configuration_response',
          upstreamUrl: `${settings.baseXApiUrl}${apiSseContract.sseStreamContract.path}`,
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        break;
      }

      case 'log': {
        this.logForWorker(message);
        break;
      }
      case 'waitingroom_status_queued': {
        this.logger.info('emit waiting room message to subscribers', {
          consultationId: message.consultationId,
        });
        this.emit(message);
        break;
      }

      default: {
        this.logger.info('emit message to subscribers', message.domain);
        this.emit(message);
        break;
      }
    }
  }

  logForWorker({ level, message, args = [] }: SseLogMessage) {
    switch (level) {
      case 'debug':
        this.workerLogger.debug(message, ...args);
        break;
      case 'info':
        this.workerLogger.info(message, ...args);
        break;
      case 'warn':
        this.workerLogger.warn(message, ...args);
        break;
      case 'error':
        this.workerLogger.error(message, ...args);
        break;
      default:
        this.workerLogger.warn('Unknown log level received from worker', message, ...args);
        break;
    }
  }
  handleError(event: ErrorEvent) {
    this.logger.error('received worker error', event);
    this.emit({ domain: 'sse_connection_status', status: 'error', detail: event.error });
  }

  emit<T extends SseEventName>(message: Message<T>) {
    this.listeners[message.domain]?.forEach((callback) => callback(message));
  }

  on<T extends SseEventName>(domain: T, callback: Listener<T>) {
    this.listeners[domain] ??= [];
    this.listeners[domain].push(callback);
  }

  off<T extends SseEventName>(domain: T, callback: Listener<T>) {
    this.listeners[domain] = this.listeners[domain]?.filter((cb) => cb !== callback) as (typeof this.listeners)[T];
  }

  close() {
    this.worker.port.close();
  }
}
